import React, { useState, useRef, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

/* ═══════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════ */

const stageColumns = [
  'Market Evaluation','Partner Evaluation','Scouting','CDA','BC','TS','Agreements','DD',
  'MA Submission','MA Approval','Prelaunch Activities','Product Launch'
];

const colorOptions = [
  { key: 'none', label: 'Pending', color: 'transparent', bg: '#e2e8f0' },
  { key: 'green', label: 'Completed', color: '#22c55e', bg: '#22c55e' },
  { key: 'yellow', label: 'In Progress', color: '#facc15', bg: '#facc15' },
  { key: 'red', label: 'Blocked', color: '#ef4444', bg: '#ef4444' }
];

const colorMap = { green:'#22c55e', yellow:'#facc15', red:'#ef4444', none:'transparent' };

const footnotesMap = {
  patches: ['* QAA, PVA, Forecast, Artwork, Importation'],
  lai: ['*Colombia Total Downpayment: $200K, additional $60K at Product Launch.','*Chile Total Downpayment: $100K at tender winning.','**Expected Annual Tender Value','* QAA, PVA, Forecast, Artwork, Importation'],
  biosimilars: []
};

const countryOptions = ['Colombia','Peru','Ecuador','Mexico','Chile','Panama','Brazil','Argentina','Uruguay','CAMCAR'];

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */

const getCurrentStage = (stages) => {
  for (let i = stageColumns.length - 1; i >= 0; i--) {
    const c = stages[stageColumns[i]];
    if (c === 'yellow' || c === 'red') return i;
  }
  for (let i = 0; i < stageColumns.length; i++) {
    if (stages[stageColumns[i]] === 'none') return Math.max(0, i - 1);
  }
  return stageColumns.length - 1;
};

const getProgress = (stages) => {
  const done = Object.values(stages).filter(c => c === 'green').length;
  return Math.round((done / stageColumns.length) * 100);
};

/* ═══════════════════════════════════════════
   CELL EDITOR MODAL
   ═══════════════════════════════════════════ */
const CellEditorModal = ({ stage, currentColor, currentText, onSave, onClose, position, dark }) => {
  const [selectedColor, setSelectedColor] = useState(currentColor || 'none');
  const [text, setText] = useState(currentText || '');
  const ref = useRef(null);

  useEffect(() => { ref.current?.focus(); }, []);

  const modalBg = dark ? '#1e293b' : '#fff';
  const borderC = dark ? '#475569' : '#cbd5e1';
  const textP = dark ? '#f1f5f9' : '#0f172a';
  const textS = dark ? '#94a3b8' : '#64748b';

  return (
    <div style={{ position:'fixed', left:Math.min(position.x, window.innerWidth - 280), top:Math.min(position.y, window.innerHeight - 260), zIndex:9999,
      background:modalBg, borderRadius:'12px', boxShadow:'0 20px 60px rgba(0,0,0,0.25)', border:`1px solid ${borderC}`, padding:'16px', width:'250px' }}>
      
      <div style={{ marginBottom:'12px' }}>
        <p style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Stage</p>
        <p style={{ fontSize:'14px', fontWeight:700, color:textP }}>{stage}</p>
      </div>

      <div style={{ marginBottom:'14px' }}>
        <p style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Status</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
          {colorOptions.map(opt => (
            <button key={opt.key} onClick={() => setSelectedColor(opt.key)}
              style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 10px', borderRadius:'8px', border: selectedColor === opt.key ? '2px solid #3b82f6' : `1px solid ${borderC}`,
                background: selectedColor === opt.key ? (dark ? '#1e40af20' : '#eff6ff') : 'transparent', cursor:'pointer', transition:'all 0.15s' }}>
              <span style={{ width:'16px', height:'16px', borderRadius:'4px', background: opt.bg, border: opt.key === 'none' ? `1px solid ${borderC}` : 'none' }}></span>
              <span style={{ fontSize:'11px', fontWeight:600, color:textP }}>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom:'14px' }}>
        <p style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Date / Note (optional)</p>
        <input ref={ref} type="text" value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onSave(selectedColor, text); if (e.key === 'Escape') onClose(); }}
          style={{ width:'100%', padding:'8px 10px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'12px', outline:'none', 
            background: dark ? '#0f172a' : '#fff', color: textP, boxSizing:'border-box' }}
          placeholder="e.g. Q1 2026, TBC, etc." />
      </div>

      <div style={{ display:'flex', gap:'8px' }}>
        <button onClick={() => onSave(selectedColor, text)} 
          style={{ flex:1, padding:'10px', background:'#0f2b46', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
          Save
        </button>
        <button onClick={onClose} 
          style={{ flex:1, padding:'10px', background: dark ? '#334155' : '#f1f5f9', color: textP, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   NEW PROJECT MODAL
   ═══════════════════════════════════════════ */
const NewProjectModal = ({ onSave, onClose, dark, existingPartners, activeTab }) => {
  const { showToast } = useToast();
  const [partner, setPartner] = useState('');
  const [product, setProduct] = useState('');
  const [country, setCountry] = useState('');
  const [stages, setStages] = useState(() => {
    const initial = {};
    stageColumns.forEach(s => { initial[s] = 'none'; });
    return initial;
  });
  const [stageTexts, setStageTexts] = useState({});
  const [downpayments, setDownpayments] = useState('');
  const [expected, setExpected] = useState('');

  const modalBg = dark ? '#1e293b' : '#fff';
  const borderC = dark ? '#475569' : '#cbd5e1';
  const textP = dark ? '#f1f5f9' : '#0f172a';
  const textS = dark ? '#94a3b8' : '#64748b';
  const inputBg = dark ? '#0f172a' : '#fff';

  const handleSave = () => {
    if (!partner.trim()) { showToast('Please enter a partner name', 'warning'); return; }
    if (!product.trim()) { showToast('Please enter a product name', 'warning'); return; }
    if (!country.trim()) { showToast('Please enter a country', 'warning'); return; }
    onSave({ partner: partner.trim(), product: product.trim(), country, stages, stageTexts, downpayments, expected });
  };

  const handleStageClick = (stage) => {
    const current = stages[stage];
    const cycle = ['none', 'green', 'yellow', 'red'];
    const idx = cycle.indexOf(current);
    const next = cycle[(idx + 1) % cycle.length];
    setStages(prev => ({ ...prev, [stage]: next }));
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)' }} onClick={onClose}></div>
      <div style={{ position:'relative', background:modalBg, borderRadius:'12px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)', border:`1px solid ${borderC}`, padding:'24px', width:'700px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        
        <h3 style={{ fontSize:'18px', fontWeight:700, color:textP, marginBottom:'20px' }}>
          ➕ Add New Project
        </h3>

        {/* Basic Info */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'20px' }}>
          <div>
            <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase' }}>Partner *</label>
            <input type="text" value={partner} onChange={e => setPartner(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background:inputBg, color:textP, boxSizing:'border-box' }}
              placeholder="e.g. Humax, Knight" />
          </div>
          <div>
            <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase' }}>Product *</label>
            <input type="text" value={product} onChange={e => setProduct(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background:inputBg, color:textP, boxSizing:'border-box' }}
              placeholder="e.g. RIV SD, Paliperidone" />
          </div>
          <div>
            <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase' }}>Country *</label>
            <input type="text" value={country} onChange={e => setCountry(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background:inputBg, color:textP, boxSizing:'border-box' }}
              placeholder="e.g. Colombia, Peru" />
          </div>
        </div>

        {/* Stages */}
        <div style={{ marginBottom:'20px' }}>
          <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'10px', display:'block', textTransform:'uppercase' }}>
            Stages (click to change status)
          </label>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:'8px' }}>
            {stageColumns.map(stage => {
              const clr = stages[stage];
              const bg = clr === 'none' ? (dark ? '#334155' : '#f1f5f9') : colorMap[clr];
              const tc = clr === 'red' ? '#fff' : clr === 'green' ? '#052e16' : clr === 'yellow' ? '#713f12' : textS;
              return (
                <div key={stage}>
                  <div onClick={() => handleStageClick(stage)}
                    style={{ background:bg, borderRadius:'6px', padding:'10px 6px', textAlign:'center', cursor:'pointer',
                      border: `1px solid ${borderC}`, transition:'all 0.15s' }}>
                    <span style={{ fontSize:'10px', fontWeight:700, color:tc }}>{stage.length > 12 ? stage.replace(' ', '\n') : stage}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize:'10px', color:textS, marginTop:'8px' }}>
            🔵 Pending → 🟢 Completed → 🟡 In Progress → 🔴 Blocked
          </p>
        </div>

        {/* Financials */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
          <div>
            <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase' }}>Downpayment 2026</label>
            <input type="text" value={downpayments} onChange={e => setDownpayments(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background:inputBg, color:textP, boxSizing:'border-box' }}
              placeholder="e.g. $700, $140K" />
          </div>
          <div>
            <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase' }}>Expected {activeTab === 'lai' ? 'Y1 Sales' : '2026'}</label>
            <input type="text" value={expected} onChange={e => setExpected(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background:inputBg, color:textP, boxSizing:'border-box' }}
              placeholder="e.g. $267, $795K" />
          </div>
        </div>

        <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
          <button onClick={onClose} 
            style={{ padding:'10px 20px', background: dark ? '#334155' : '#f1f5f9', color: textP, border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} 
            style={{ padding:'10px 20px', background:'#0f2b46', color:'#fff', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
            Create Project
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PARTNER UPDATE EDITOR MODAL
   ═══════════════════════════════════════════ */
const PartnerUpdateModal = ({ update, onSave, onDelete, onClose, dark, isNew, projectOptions }) => {
  const { showToast } = useToast();
  const [partner, setPartner] = useState(update?.partner || '');
  const [notesText, setNotesText] = useState(update?.notes?.join('\n') || '');

  const modalBg = dark ? '#1e293b' : '#fff';
  const borderC = dark ? '#475569' : '#cbd5e1';
  const textP = dark ? '#f1f5f9' : '#0f172a';
  const textS = dark ? '#94a3b8' : '#64748b';

  const handleSave = () => {
    if (!partner.trim()) { showToast('Please select a project', 'warning'); return; }
    const notes = notesText.split('\n').map(n => n.trim()).filter(n => n);
    onSave({ ...update, partner: partner.trim(), notes });
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)' }} onClick={onClose}></div>
      <div style={{ position:'relative', background:modalBg, borderRadius:'12px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)', border:`1px solid ${borderC}`, padding:'20px', width:'450px', maxWidth:'90vw' }}>
        
        <h3 style={{ fontSize:'16px', fontWeight:700, color:textP, marginBottom:'16px' }}>
          {isNew ? '➕ Add Project Update' : '✏️ Edit Project Update'}
        </h3>

        <div style={{ marginBottom:'14px' }}>
          <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase', letterSpacing:'0.05em' }}>Project Name</label>
          {isNew ? (
            <select value={partner} onChange={e => setPartner(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background: dark ? '#0f172a' : '#fff', color: textP, boxSizing:'border-box', cursor:'pointer' }}>
              <option value="">— Select a project —</option>
              {projectOptions.map((p, i) => <option key={i} value={p}>{p}</option>)}
            </select>
          ) : (
            <input type="text" value={partner} readOnly
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'13px', outline:'none', 
                background: dark ? '#0f172a' : '#fff', color: textP, boxSizing:'border-box', opacity:0.7 }} />
          )}
        </div>

        <div style={{ marginBottom:'16px' }}>
          <label style={{ fontSize:'11px', fontWeight:700, color:textS, marginBottom:'6px', display:'block', textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes (one per line)</label>
          <textarea value={notesText} onChange={e => setNotesText(e.target.value)}
            style={{ width:'100%', padding:'10px 12px', border:`1px solid ${borderC}`, borderRadius:'8px', fontSize:'12px', outline:'none', 
              background: dark ? '#0f172a' : '#fff', color: textP, boxSizing:'border-box', minHeight:'120px', resize:'vertical', lineHeight:'1.5' }}
            placeholder="Enter each note on a new line&#10;Example: Pending BC Approval.&#10;Another note here." />
        </div>

        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={handleSave} 
            style={{ flex:1, padding:'10px', background:'#0f2b46', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
            {isNew ? 'Add Update' : 'Save Changes'}
          </button>
          {!isNew && onDelete && (
            <button onClick={() => { if(window.confirm('Delete this project update?')) onDelete(update.id); }} 
              style={{ padding:'10px 16px', background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
              🗑️ Delete
            </button>
          )}
          <button onClick={onClose} 
            style={{ padding:'10px 16px', background: dark ? '#334155' : '#f1f5f9', color: textP, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   TOOLTIP
   ═══════════════════════════════════════════ */
const TooltipWrap = ({ text, stage, color, children }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
    setShow(true);
  };

  const label = color === 'green' ? 'Completed' : color === 'yellow' ? 'In Progress' : color === 'red' ? 'Blocked' : 'Pending';

  return (
    <div onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)} style={{ position:'relative' }}>
      {children}
      {show && (
        <div style={{ position:'fixed', left:pos.x, top:pos.y, transform:'translate(-50%,-100%)', zIndex:9997,
          background:'#1e293b', color:'#fff', padding:'6px 10px', borderRadius:'6px', boxShadow:'0 4px 12px rgba(0,0,0,0.2)',
          fontSize:'11px', pointerEvents:'none', whiteSpace:'nowrap' }}>
          <div style={{ fontWeight:700 }}>{stage}</div>
          <div style={{ opacity:0.7 }}>{label}{text ? ` · ${text}` : ''}</div>
          <div style={{ position:'absolute', bottom:'-4px', left:'50%', transform:'translateX(-50%)',
            borderLeft:'5px solid transparent', borderRight:'5px solid transparent', borderTop:'5px solid #1e293b' }}></div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   EXPANDED ROW
   ═══════════════════════════════════════════ */
const ExpandedRow = ({ proj, notes, colSpan, dark, onDelete }) => {
  const progress = getProgress(proj.stages);
  const currentIdx = getCurrentStage(proj.stages);
  const bg = dark ? '#070d1a' : '#dce4ef';
  const cardBg = dark ? '#0f172a' : '#f8fafc';
  const bdr = dark ? '#1e293b' : '#c8d4e2';
  const tp = dark ? '#f1f5f9' : '#0f172a';
  const ts = dark ? '#94a3b8' : '#64748b';
  const tm = dark ? '#64748b' : '#94a3b8';

  return (
    <tr><td colSpan={colSpan} style={{ padding:0, background:bg }}>
      <div style={{ padding:'14px 20px', borderTop:`1px solid ${bdr}`, borderBottom:`2px solid ${bdr}` }}>
        <div style={{ display:'flex', gap:'3px', marginBottom:'14px' }}>
          {stageColumns.map((stage, i) => {
            const clr = proj.stages[stage];
            const cbg = clr === 'none' ? (dark?'#334155':'#e2e8f0') : colorMap[clr];
            const isCur = i === currentIdx;
            const txt = proj.stageTexts[stage];
            return (
              <div key={stage} style={{ flex:1, textAlign:'center' }}>
                <div style={{ height:'7px', borderRadius:'4px', background:cbg,
                  outline: isCur ? `2px solid ${dark?'#60a5fa':'#1e40af'}` : 'none', outlineOffset:'2px', transition:'all 0.2s' }}></div>
                <div style={{ fontSize:'8px', marginTop:'4px', color: isCur ? tp : tm, fontWeight: isCur ? 800 : 400, lineHeight:'1.2' }}>
                  {stage.length > 12 ? stage.replace(/(Activities|Submission|Approval|Evaluation)/,' $1') : stage}
                </div>
                {txt && <div style={{ fontSize:'8px', color:ts, fontWeight:700 }}>{txt}</div>}
              </div>
            );
          })}
        </div>

        <div style={{ display:'flex', gap:'12px' }}>
          <div style={{ background:cardBg, borderRadius:'8px', padding:'10px 14px', border:`1px solid ${bdr}`, minWidth:'120px' }}>
            <div style={{ fontSize:'9px', color:tm, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>Progress</div>
            <div style={{ fontSize:'26px', fontWeight:700, color:tp, lineHeight:1 }}>{progress}%</div>
            <div style={{ marginTop:'6px', height:'5px', borderRadius:'3px', background:dark?'#334155':'#e2e8f0', overflow:'hidden' }}>
              <div style={{ width:`${progress}%`, height:'100%', borderRadius:'3px',
                background: progress===100?'#22c55e':progress>60?'#3b82f6':'#facc15', transition:'width 0.5s' }}></div>
            </div>
          </div>

          <div style={{ background:cardBg, borderRadius:'8px', padding:'10px 14px', border:`1px solid ${bdr}`, minWidth:'160px' }}>
            <div style={{ fontSize:'9px', color:tm, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Financials</div>
            <div style={{ display:'flex', gap:'16px' }}>
              <div><div style={{ fontSize:'9px', color:ts }}>Downpayment</div><div style={{ fontSize:'15px', fontWeight:700, color:tp }}>{proj.downpayments||'—'}</div></div>
              <div><div style={{ fontSize:'9px', color:ts }}>Expected</div><div style={{ fontSize:'15px', fontWeight:700, color:tp }}>{proj.expected||'—'}</div></div>
            </div>
          </div>

          {notes.length > 0 && (
            <div style={{ background:cardBg, borderRadius:'8px', padding:'10px 14px', border:`1px solid ${bdr}`, flex:1 }}>
              <div style={{ fontSize:'9px', color:tm, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>
                Partner Notes — {proj.partner}
              </div>
              {notes.map((n, i) => (
                <div key={i} style={{ fontSize:'10px', color:ts, marginBottom:'2px', lineHeight:'1.4' }}>
                  {n}
                </div>
              ))}
            </div>
          )}

          <div style={{ display:'flex', alignItems:'flex-end', marginLeft:'auto' }}>
            <button onClick={(e) => { e.stopPropagation(); if(window.confirm(`Delete project "${proj.product} — ${proj.country}"?`)) onDelete(proj.id); }}
              style={{ padding:'10px 16px', background:'#fee2e2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:'8px', fontSize:'12px', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', whiteSpace:'nowrap', transition:'all 0.15s' }}
              onMouseEnter={e=>{e.currentTarget.style.background='#fca5a5';e.currentTarget.style.color='#7f1d1d';}}
              onMouseLeave={e=>{e.currentTarget.style.background='#fee2e2';e.currentTarget.style.color='#dc2626';}}>
              🗑️ Delete Project
            </button>
          </div>
        </div>
      </div>
    </td></tr>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
// Map a DB row (snake_case) to the shape the component's render code expects.
const rowFromDb = (row) => ({
  id: row.id,
  project: row.project_no,
  partner: row.partner,
  product: row.product,
  country: row.country,
  stages: row.stages || {},
  stageTexts: row.stage_texts || {},
  downpayments: row.downpayments || '',
  expected: row.expected || '',
});

const noteFromDb = (row) => ({
  id: row.id,
  partner: row.partner,
  notes: row.notes || [],
});

export function ProjectMilestones() {
  const { showToast } = useToast();
  const [activeTab, setActiveTabRaw] = useState(() => localStorage.getItem('luye_deals_tab') || 'patches');
  const setActiveTab = (v) => { localStorage.setItem('luye_deals_tab', v); setActiveTabRaw(v); };
  const [patches, setPatches] = useState([]);
  const [lai, setLAI] = useState([]);
  const [biosimilars, setBiosimilars] = useState([]);
  const [partnerNotes, setPartnerNotes] = useState({ patches: [], lai: [], biosimilars: [] });
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState('');
  const [dark, setDark] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Load everything from Supabase on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [projectsRes, notesRes] = await Promise.all([
        supabase.from('projects').select('*').order('project_no', { ascending: true, nullsFirst: false }).order('created_at', { ascending: true }),
        supabase.from('partner_notes').select('*').order('created_at', { ascending: true }),
      ]);
      if (cancelled) return;
      if (projectsRes.error) { console.error('projects load:', projectsRes.error); setSaveError('Failed to load projects'); }
      if (notesRes.error) { console.error('partner_notes load:', notesRes.error); setSaveError('Failed to load partner notes'); }

      const projects = projectsRes.data || [];
      setPatches(projects.filter(r => r.tab === 'patches').map(rowFromDb));
      setLAI(projects.filter(r => r.tab === 'lai').map(rowFromDb));
      setBiosimilars(projects.filter(r => r.tab === 'biosimilars').map(rowFromDb));

      const notes = notesRes.data || [];
      setPartnerNotes({
        patches: notes.filter(n => n.tab === 'patches').map(noteFromDb),
        lai: notes.filter(n => n.tab === 'lai').map(noteFromDb),
        biosimilars: notes.filter(n => n.tab === 'biosimilars').map(noteFromDb),
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);
  
  const [editingCell, setEditingCell] = useState(null);
  const [editorPos, setEditorPos] = useState({ x:0, y:0 });
  
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [isNewUpdate, setIsNewUpdate] = useState(false);
  
  const [showNewProject, setShowNewProject] = useState(false);
  
  const [filterPartner, setFilterPartner] = useState('All');
  const [filterCountry, setFilterCountry] = useState('All');
  const [filterProduct, setFilterProduct] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  
  const [collapsedPartners, setCollapsedPartners] = useState({});
  const [showProgress, setShowProgress] = useState(true);
  const exportRef = useRef(null);

  const handleExportPDF = () => {
    const el = exportRef.current;
    if (!el) return;

    // Hide no-print elements
    const hidden = el.querySelectorAll('.no-print');
    hidden.forEach(h => { h.dataset.prevDisplay = h.style.display; h.style.display = 'none'; });

    const printContent = el.innerHTML;

    // Restore
    hidden.forEach(h => { h.style.display = h.dataset.prevDisplay || ''; delete h.dataset.prevDisplay; });

    const printWindow = window.open('', '_blank');
    if (!printWindow) { showToast('Please allow popups to export PDF', 'warning'); return; }

    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Project Seaton — ${tabTitle}</title>
      <style>
        @page { size: landscape; margin: 8mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        body { font-family: -apple-system,BlinkMacSystemFont,'Segoe UI','Roboto','Helvetica Neue',sans-serif; margin: 0; padding: 10px; background: ${dark ? '#0b1120' : '#f0f4f8'}; }
        table { width: 100%; border-collapse: collapse; min-width: 0 !important; }
        input { border: none !important; background: transparent !important; }
        button { display: none !important; }
      </style>
    </head><body>${printContent}</body></html>`);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const getData = () => activeTab==='patches'?patches:activeTab==='lai'?lai:biosimilars;
  const setData = (d) => { 
    if(activeTab==='patches') setPatches(d);
    else if(activeTab==='lai') setLAI(d);
    else setBiosimilars(d);
  };
  const allData = getData();
  const notes = partnerNotes[activeTab]||[];
  const footnotes = footnotesMap[activeTab]||[];

  const partners = useMemo(()=>[...new Set(allData.map(d=>d.partner))],[allData]);
  const countries = useMemo(()=>[...new Set(allData.map(d=>d.country))],[allData]);
  const products = useMemo(()=>[...new Set(allData.map(d=>d.product))],[allData]);

  const filteredData = useMemo(()=>{
    return allData.filter(d=>{
      if(filterPartner!=='All'&&d.partner!==filterPartner) return false;
      if(filterCountry!=='All'&&d.country!==filterCountry) return false;
      if(filterProduct!=='All'&&d.product!==filterProduct) return false;
      if(filterStatus==='Blocked'&&!Object.values(d.stages).includes('red')) return false;
      if(filterStatus==='In Progress'&&!Object.values(d.stages).includes('yellow')) return false;
      if(filterStatus==='On Track'&&(Object.values(d.stages).includes('red')||Object.values(d.stages).includes('yellow'))) return false;
      return true;
    });
  },[allData,filterPartner,filterCountry,filterProduct,filterStatus]);

  const grouped = useMemo(()=>{
    const m={};filteredData.forEach(d=>{if(!m[d.partner])m[d.partner]=[];m[d.partner].push(d);});return m;
  },[filteredData]);

  const totalProjects = filteredData.length;
  const greenCount = filteredData.reduce((s,p)=>s+Object.values(p.stages).filter(c=>c==='green').length,0);
  const yellowCount = filteredData.reduce((s,p)=>s+Object.values(p.stages).filter(c=>c==='yellow').length,0);
  const redCount = filteredData.reduce((s,p)=>s+Object.values(p.stages).filter(c=>c==='red').length,0);

  const handleCellClick = (e, pid, stage) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setEditorPos({ x: rect.left, y: rect.bottom + 4 });
    const proj = getData().find(p => p.id === pid);
    setEditingCell({ projectId: pid, stage, color: proj.stages[stage], text: proj.stageTexts[stage] || '' });
  };

  const handleSaveCell = async (color, text) => {
    if (!editingCell) return;
    const d = getData();
    const target = d.find(p => p.id === editingCell.projectId);
    if (!target) { setEditingCell(null); return; }
    const newStages = { ...target.stages, [editingCell.stage]: color };
    const newTexts = { ...target.stageTexts };
    if (text.trim()) newTexts[editingCell.stage] = text.trim();
    else delete newTexts[editingCell.stage];

    setData(d.map(p => p.id === editingCell.projectId ? { ...p, stages: newStages, stageTexts: newTexts } : p));
    setEditingCell(null);

    const { error } = await supabase.from('projects')
      .update({ stages: newStages, stage_texts: newTexts })
      .eq('id', editingCell.projectId);
    if (error) { console.error('handleSaveCell:', error); setSaveError('Failed to save cell'); }
  };

  const handleFinEdit = async (pid, field, val) => {
    const d = getData();
    setData(d.map(p => p.id === pid ? { ...p, [field]: val } : p));
    const { error } = await supabase.from('projects').update({ [field]: val }).eq('id', pid);
    if (error) { console.error('handleFinEdit:', error); setSaveError('Failed to save change'); }
  };

  const toggleCollapse = (p) => setCollapsedPartners(prev => ({ ...prev, [p]: !prev[p] }));

  const resetFilters = () => { setFilterPartner('All'); setFilterCountry('All'); setFilterProduct('All'); setFilterStatus('All'); };
  const hasFilters = filterPartner !== 'All' || filterCountry !== 'All' || filterProduct !== 'All' || filterStatus !== 'All';

  // New project
  const handleCreateProject = async (projectData) => {
    const d = getData();
    const maxProject = d.length > 0 ? Math.max(...d.map(p => p.project || 0)) : 0;
    const projectNo = activeTab === 'patches' ? maxProject + 1 : null;

    const { data, error } = await supabase.from('projects').insert({
      tab: activeTab,
      project_no: projectNo,
      partner: projectData.partner,
      product: projectData.product,
      country: projectData.country,
      stages: projectData.stages,
      stage_texts: projectData.stageTexts,
      downpayments: projectData.downpayments || '',
      expected: projectData.expected || '',
    }).select().single();

    if (error) { console.error('handleCreateProject:', error); setSaveError('Failed to create project'); return; }
    setData([...d, rowFromDb(data)]);
    setShowNewProject(false);
  };

  // Partner updates
  const handleAddUpdate = () => {
    setEditingUpdate({ id: Date.now(), partner: '', notes: [] });
    setIsNewUpdate(true);
  };

  const handleEditUpdate = (update) => {
    setEditingUpdate(update);
    setIsNewUpdate(false);
  };

  const handleSaveUpdate = async (updatedData) => {
    if (isNewUpdate) {
      const { data, error } = await supabase.from('partner_notes').insert({
        tab: activeTab,
        partner: updatedData.partner,
        notes: updatedData.notes,
      }).select().single();
      if (error) { console.error('handleSaveUpdate insert:', error); setSaveError('Failed to save update'); return; }
      setPartnerNotes(prev => ({ ...prev, [activeTab]: [...(prev[activeTab] || []), noteFromDb(data)] }));
    } else {
      const { error } = await supabase.from('partner_notes')
        .update({ partner: updatedData.partner, notes: updatedData.notes })
        .eq('id', updatedData.id);
      if (error) { console.error('handleSaveUpdate update:', error); setSaveError('Failed to save update'); return; }
      setPartnerNotes(prev => {
        const tabNotes = [...(prev[activeTab] || [])];
        const idx = tabNotes.findIndex(n => n.id === updatedData.id);
        if (idx >= 0) tabNotes[idx] = updatedData;
        return { ...prev, [activeTab]: tabNotes };
      });
    }
    setEditingUpdate(null);
  };

  const handleDeleteUpdate = async (id) => {
    setPartnerNotes(prev => ({
      ...prev,
      [activeTab]: (prev[activeTab] || []).filter(n => n.id !== id)
    }));
    setEditingUpdate(null);
    const { error } = await supabase.from('partner_notes').delete().eq('id', id);
    if (error) { console.error('handleDeleteUpdate:', error); setSaveError('Failed to delete update'); }
  };

  const handleDeleteProject = async (id) => {
    setData(getData().filter(p => p.id !== id));
    setExpandedRow(null);
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) { console.error('handleDeleteProject:', error); setSaveError('Failed to delete project'); }
  };

  const t = {
    pageBg:dark?'#0b1120':'#f0f4f8', cardBg:dark?'#1e293b':'#fff', bdr:dark?'#334155':'#e2e8f0',
    hdr:'#0f2b46', tp:dark?'#f1f5f9':'#0f172a', ts:dark?'#94a3b8':'#475569', tm:dark?'#64748b':'#94a3b8',
    rowE:dark?'#1e293b':'#fff', rowO:dark?'#172033':'#f8fafc', rowH:dark?'rgba(59,130,246,0.08)':'rgba(59,130,246,0.04)',
    partHdr:dark?'#162032':'#eef2f7', emptyBg:dark?'#111827':'#f8fafc', inputBg:dark?'#0f172a':'transparent',
    cellNone:dark?'#253145':'#f1f5f9', selectBg:dark?'#1e293b':'#fff',
  };

  const tabTitle = activeTab==='patches'?'Patch Projects Milestones':activeTab==='lai'?'LAI Projects Milestones':'Biosimilars Projects Milestones';
  const colSpan = (activeTab==='patches'?1:0)+3+stageColumns.length+(showProgress?1:0)+2;

  const SelectFilter = ({ label, value, onChange, options }) => (
    <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
      <label style={{ fontSize:'10px', fontWeight:700, color:t.tm, textTransform:'uppercase', letterSpacing:'0.03em' }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ padding:'6px 10px', borderRadius:'6px', border:`1px solid ${t.bdr}`, fontSize:'12px', fontWeight:600,
          background:t.selectBg, color:t.tp, cursor:'pointer', outline:'none', minWidth:'120px' }}>
        <option value="All">All</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
        <div style={{ width:'48px', height:'48px', border:'4px solid #3b82f6', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div ref={exportRef} style={{background:t.pageBg,minHeight:'100vh',padding:'20px',transition:'all 0.3s'}}>
      {saveError && (
        <div className="no-print" onClick={() => setSaveError('')} style={{ position:'fixed', top:16, right:16, zIndex:10000, background:'#fee2e2', color:'#991b1b', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:600, cursor:'pointer', boxShadow:'0 8px 24px rgba(0,0,0,0.15)' }}>
          {saveError} — click to dismiss
        </div>
      )}
      <style>{`
        @media print{
          .no-print{display:none!important}
          @page{size:landscape;margin:10mm}
          body,html{background:#fff!important;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
          *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
          table{font-size:10px!important;width:100%!important;min-width:0!important}
          th,td{padding:4px 3px!important}
          div[style*="overflowX"]{overflow:visible!important}
        }
        @keyframes pulseRing{0%,100%{box-shadow:0 0 0 0 rgba(59,130,246,0.4)}50%{box-shadow:0 0 0 3px rgba(59,130,246,0)}}
      `}</style>

      <div style={{maxWidth:'100%',margin:'0 auto'}}>

        {/* HEADER */}
        <div className="no-print" style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'16px',flexWrap:'wrap',gap:'10px'}}>
          <div>
            <h1 style={{color:t.tp,fontSize:'24px',fontWeight:700,margin:0}}>Project Seaton — Monthly Review</h1>
            <p style={{color:t.tm,fontSize:'14px',marginTop:'2px'}}>Luye Pharma LATAM · Pipeline Milestones Tracker</p>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
            <div onClick={()=>setShowProgress(!showProgress)} style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',userSelect:'none'}}>
              <span style={{fontSize:'12px',fontWeight:700,color:t.tp}}>📊</span>
              <div style={{width:'44px',height:'24px',borderRadius:'12px',background:showProgress?'#22c55e':'#94a3b8',
                position:'relative',transition:'background 0.3s',boxShadow:'inset 0 1px 3px rgba(0,0,0,0.2)'}}>
                <div style={{width:'18px',height:'18px',borderRadius:'50%',background:'#fff',position:'absolute',top:'3px',
                  left:showProgress?'23px':'3px',transition:'left 0.3s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)'}}></div>
              </div>
              <span style={{fontSize:'11px',fontWeight:600,color:t.ts}}>Progress</span>
            </div>
            <div onClick={()=>setDark(!dark)} style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',userSelect:'none'}}>
              <span style={{fontSize:'12px',fontWeight:700,color:t.tp}}>{dark?'🌙':'☀️'}</span>
              <div style={{width:'44px',height:'24px',borderRadius:'12px',background:dark?'#3b82f6':'#94a3b8',
                position:'relative',transition:'background 0.3s',boxShadow:'inset 0 1px 3px rgba(0,0,0,0.2)'}}>
                <div style={{width:'18px',height:'18px',borderRadius:'50%',background:'#fff',position:'absolute',top:'3px',
                  left:dark?'23px':'3px',transition:'left 0.3s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)'}}></div>
              </div>
              <span style={{fontSize:'11px',fontWeight:600,color:t.ts}}>{dark?'Dark':'Light'}</span>
            </div>
            <button onClick={handleExportPDF} style={{padding:'6px 12px',borderRadius:'6px',border:'none',
              background:'#0f2b46',color:'#fff',fontSize:'12px',cursor:'pointer',fontWeight:600,
              display:'flex',alignItems:'center',gap:'6px'}}>📄 Export PDF</button>
          </div>
        </div>

        {/* TABS */}
        <div className="no-print" style={{display:'flex',gap:'2px'}}>
          {[{k:'patches',l:'🩹 Patches'},{k:'lai',l:'💉 LAI'},{k:'biosimilars',l:'🧬 Biosimilars'}].map(tb=>(
            <button key={tb.k} onClick={()=>{setActiveTab(tb.k);setExpandedRow(null);resetFilters();}}
              style={{padding:'10px 20px',borderRadius:'8px 8px 0 0',fontSize:'13px',fontWeight:700,cursor:'pointer',border:'none',
                background:activeTab===tb.k?t.cardBg:(dark?'#162032':'#e2e8f0'),
                color:activeTab===tb.k?t.tp:t.tm,transition:'all 0.2s',
                boxShadow:activeTab===tb.k?'0 -2px 8px rgba(0,0,0,0.06)':'none'}}>{tb.l}</button>
          ))}
        </div>

        {/* MAIN CARD */}
        <div style={{background:t.cardBg,borderRadius:'0 8px 8px 8px',boxShadow:'0 4px 20px rgba(0,0,0,0.08)',
          border:`1px solid ${t.bdr}`,overflow:'hidden',transition:'all 0.3s'}}>

          <div style={{padding:'14px 20px 14px',borderBottom:`1px solid ${t.bdr}`}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
              <div>
                <h2 style={{color:t.tp,fontSize:'18px',fontWeight:700,margin:'0 0 4px'}}>{tabTitle}</h2>
                <p style={{color:t.tm,fontSize:'11px'}}>Click cell to edit status & date · Click row to expand details</p>
              </div>
              <button className="no-print" onClick={() => setShowNewProject(true)}
                style={{padding:'8px 16px',background:'#0f2b46',color:'#fff',border:'none',borderRadius:'6px',fontSize:'12px',fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px'}}>
                ➕ New Project
              </button>
            </div>

            <div className="no-print" style={{display:'flex',gap:'12px',alignItems:'flex-end',flexWrap:'wrap'}}>
              <SelectFilter label="Status" value={filterStatus} onChange={setFilterStatus} options={['On Track','In Progress','Blocked']} />
              <SelectFilter label="Partner" value={filterPartner} onChange={setFilterPartner} options={partners} />
              <SelectFilter label="Country" value={filterCountry} onChange={setFilterCountry} options={countries} />
              {products.length > 1 && <SelectFilter label="Product" value={filterProduct} onChange={setFilterProduct} options={products} />}
              {hasFilters && (
                <button onClick={resetFilters} style={{padding:'6px 12px',fontSize:'11px',color:'#ef4444',background:'#fee2e2',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:700,marginBottom:'2px'}}>
                  ✕ Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* TABLE */}
          {filteredData.length===0?(
            <div style={{padding:'60px',textAlign:'center',background:t.emptyBg}}>
              <p style={{fontSize:'15px',fontWeight:700,color:t.tm}}>{allData.length===0?'No projects yet. Click "New Project" to add one.':'No projects match filters'}</p>
              {hasFilters&&<button onClick={resetFilters} style={{marginTop:'6px',fontSize:'12px',color:'#3b82f6',background:'none',border:'none',cursor:'pointer',fontWeight:600}}>Clear filters</button>}
            </div>
          ):(
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',minWidth:'1100px'}}>
                <thead>
                  <tr style={{background:t.hdr}}>
                    {activeTab==='patches'&&<th style={{padding:'10px 8px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'center',borderRight:'1px solid rgba(255,255,255,0.1)',width:'36px'}}>#</th>}
                    <th style={{padding:'10px 12px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'left',borderRight:'1px solid rgba(255,255,255,0.1)',minWidth:'90px'}}>Product</th>
                    <th style={{padding:'10px 12px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'left',borderRight:'1px solid rgba(255,255,255,0.1)',minWidth:'90px'}}>Country</th>
                    {stageColumns.map(c=>(
                      <th key={c} style={{padding:'10px 4px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'center',
                        borderRight:'1px solid rgba(255,255,255,0.1)',minWidth:'62px',wordWrap:'break-word',whiteSpace:'normal',lineHeight:'1.3'}}>
                        {c==='Prelaunch Activities'?'Prelaunch':c==='MA Submission'?'MA Sub.':c==='MA Approval'?'MA Appr.':c==='Market Evaluation'?'Market Eval.':c==='Partner Evaluation'?'Partner Eval.':c}
                      </th>
                    ))}
                    {showProgress&&<th style={{padding:'10px 8px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'center',borderRight:'1px solid rgba(255,255,255,0.1)',minWidth:'120px'}}>Progress</th>}
                    <th style={{padding:'10px 4px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'center',borderRight:'1px solid rgba(255,255,255,0.1)',width:'75px'}}>Downpayment</th>
                    <th style={{padding:'10px 4px',fontSize:'13px',fontWeight:700,color:'#fff',textAlign:'center',width:'75px',whiteSpace:'normal',lineHeight:'1.3'}}>Expected Sales 5 yrs</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(grouped).map(([partner,projList])=>{
                    const collapsed=collapsedPartners[partner];
                    return(
                      <React.Fragment key={partner}>
                        <tr onClick={()=>toggleCollapse(partner)} style={{background:t.partHdr,cursor:'pointer',userSelect:'none'}}>
                          <td colSpan={colSpan} style={{padding:'8px 12px',borderBottom:`1px solid ${t.bdr}`}}>
                            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                              <span style={{fontSize:'12px',transition:'transform 0.2s',display:'inline-block',
                                transform:collapsed?'rotate(-90deg)':'rotate(0deg)',color:t.ts}}>▼</span>
                              <span style={{fontSize:'13px',fontWeight:700,color:t.tp}}>{partner}</span>
                              <span style={{fontSize:'11px',color:t.tm,fontWeight:600}}>{projList.length} project{projList.length>1?'s':''}</span>
                            </div>
                          </td>
                        </tr>

                        {!collapsed&&projList.map((proj,idx)=>{
                          const progress=getProgress(proj.stages);
                          const curIdx=getCurrentStage(proj.stages);
                          const isExp=expandedRow===proj.id;
                          const rBg=idx%2===0?t.rowE:t.rowO;
                          const pNotes=(notes.find(n=>n.partner.split('—')[0].trim()===proj.partner)||{}).notes||[];

                          return(
                            <React.Fragment key={proj.id}>
                              <tr onClick={()=>setExpandedRow(isExp?null:proj.id)}
                                style={{background:rBg,cursor:'pointer',transition:'background 0.15s',
                                  borderLeft:isExp?'3px solid #3b82f6':'3px solid transparent'}}
                                onMouseEnter={e=>{e.currentTarget.style.background=t.rowH}}
                                onMouseLeave={e=>{e.currentTarget.style.background=rBg}}>

                                {activeTab==='patches'&&<td style={{padding:'8px 8px',fontSize:'12px',textAlign:'center',fontWeight:700,color:t.tm,borderRight:`1px solid ${t.bdr}`,borderBottom:`1px solid ${t.bdr}`}}>{proj.project}</td>}
                                <td style={{padding:'8px 12px',fontSize:'12px',fontWeight:600,color:t.tp,whiteSpace:'nowrap',borderRight:`1px solid ${t.bdr}`,borderBottom:`1px solid ${t.bdr}`}}>{proj.product}</td>
                                <td style={{padding:'8px 12px',fontSize:'12px',color:t.ts,whiteSpace:'nowrap',borderRight:`1px solid ${t.bdr}`,borderBottom:`1px solid ${t.bdr}`}}>{proj.country}</td>

                                {stageColumns.map((stage,si)=>{
                                  const clr=proj.stages[stage];
                                  const txt=proj.stageTexts[stage];
                                  const bg=clr==='none'?t.cellNone:colorMap[clr];
                                  const tc=clr==='red'?'#fff':clr==='green'?'#052e16':clr==='yellow'?'#713f12':t.tp;
                                  const isCur=si===curIdx&&clr!=='none';
                                  return(
                                    <td key={stage} onClick={e=>handleCellClick(e,proj.id,stage)}
                                      style={{padding:'3px',borderRight:`1px solid ${t.bdr}`,borderBottom:`1px solid ${t.bdr}`,cursor:'pointer',userSelect:'none'}}>
                                      <TooltipWrap text={txt} stage={stage} color={clr}>
                                        <div style={{background:bg,borderRadius:'4px',minHeight:'36px',display:'flex',flexDirection:'column',
                                          alignItems:'center',justifyContent:'center',padding:'4px 3px',transition:'all 0.15s',
                                          animation:isCur?'pulseRing 2s infinite':'none',
                                          outline:isCur?`2px solid ${dark?'#60a5fa':'#1e40af'}`:'none',outlineOffset:'1px'}}>
                                          {txt&&<span style={{fontSize:'11px',fontWeight:700,color:tc,lineHeight:'1.2',textAlign:'center'}}>{txt}</span>}
                                        </div>
                                      </TooltipWrap>
                                    </td>
                                  );
                                })}

                                {showProgress&&<td style={{padding:'6px 10px',borderRight:`1px solid ${t.bdr}`,borderBottom:`1px solid ${t.bdr}`}}>
                                  <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                                    <div style={{flex:1,height:'12px',borderRadius:'6px',background:dark?'#334155':'#e2e8f0',overflow:'hidden'}}>
                                      <div style={{width:`${progress}%`,height:'100%',borderRadius:'6px',
                                        background:progress===100?'#22c55e':progress>60?'#3b82f6':'#facc15',transition:'width 0.4s'}}></div>
                                    </div>
                                    <span style={{fontSize:'11px',fontWeight:700,color:t.tp,minWidth:'32px',textAlign:'right'}}>{progress}%</span>
                                  </div>
                                </td>}

                                <td style={{padding:'4px 3px',borderRight:`1px solid ${t.bdr}`,borderBottom:`1px solid ${t.bdr}`,width:'75px'}} onClick={e=>e.stopPropagation()}>
                                  <input type="text" value={proj.downpayments} onChange={e=>handleFinEdit(proj.id,'downpayments',e.target.value)}
                                    style={{width:'100%',fontSize:'12px',textAlign:'center',fontWeight:600,color:t.tp,background:t.inputBg,border:'none',outline:'none',padding:'4px 2px',borderRadius:'4px',boxSizing:'border-box'}} placeholder="—" />
                                </td>
                                <td style={{padding:'4px 3px',borderBottom:`1px solid ${t.bdr}`,width:'75px'}} onClick={e=>e.stopPropagation()}>
                                  <input type="text" value={proj.expected} onChange={e=>handleFinEdit(proj.id,'expected',e.target.value)}
                                    style={{width:'100%',fontSize:'12px',textAlign:'center',fontWeight:600,color:t.tp,background:t.inputBg,border:'none',outline:'none',padding:'4px 2px',borderRadius:'4px',boxSizing:'border-box'}} placeholder="—" />
                                </td>
                              </tr>
                              {isExp&&<ExpandedRow proj={proj} notes={pNotes} colSpan={colSpan} dark={dark} onDelete={handleDeleteProject}/>}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {footnotes.length>0&&(
            <div style={{padding:'8px 20px',borderTop:`1px solid ${t.bdr}`}}>
              {footnotes.map((fn,i)=><p key={i} style={{fontSize:'10px',color:t.tm,fontStyle:'italic',margin:'2px 0'}}>{fn}</p>)}
            </div>
          )}
        </div>

        {/* PARTNER UPDATES */}
        <div style={{marginTop:'20px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
            <h3 style={{color:t.tp,fontSize:'15px',fontWeight:700,margin:0}}>Project Updates</h3>
            <button className="no-print" onClick={handleAddUpdate}
              style={{padding:'8px 14px',background:'#0f2b46',color:'#fff',border:'none',borderRadius:'6px',fontSize:'12px',fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px'}}>
              ➕ Add Update
            </button>
          </div>
          
          {notes.length === 0 ? (
            <div style={{background:t.cardBg,borderRadius:'8px',border:`1px solid ${t.bdr}`,padding:'30px',textAlign:'center'}}>
              <p style={{color:t.tm,fontSize:'13px'}}>No project updates yet. Click "Add Update" to create one.</p>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(notes.length,3)},1fr)`,gap:'12px'}}>
              {notes.map((n)=>(
                <div key={n.id} onClick={() => handleEditUpdate(n)}
                  style={{background:t.cardBg,borderRadius:'8px',border:`1px solid ${t.bdr}`,padding:'14px 16px',
                    boxShadow:'0 2px 8px rgba(0,0,0,0.04)',transition:'all 0.2s',cursor:'pointer'}}
                  onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)';e.currentTarget.style.borderColor='#3b82f6';}}
                  onMouseLeave={e=>{e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.04)';e.currentTarget.style.borderColor=t.bdr;}}>
                  <div style={{marginBottom:'10px',paddingBottom:'10px',borderBottom:`1px solid ${t.bdr}`}}>
                    <h4 style={{fontSize:'13px',fontWeight:700,color:t.tp,margin:0}}>{n.partner}</h4>
                  </div>
                  {n.notes.map((note,j)=>(
                    <p key={j} style={{fontSize:'11px',color:t.ts,margin:'0 0 4px',lineHeight:'1.5'}}>
                      {note}
                    </p>
                  ))}

                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CELL EDITOR MODAL */}
      {editingCell && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:9998}} onClick={()=>setEditingCell(null)}></div>
          <CellEditorModal 
            stage={editingCell.stage}
            currentColor={editingCell.color}
            currentText={editingCell.text}
            position={editorPos}
            dark={dark}
            onSave={handleSaveCell}
            onClose={()=>setEditingCell(null)}
          />
        </>
      )}

      {/* NEW PROJECT MODAL */}
      {showNewProject && (
        <NewProjectModal
          dark={dark}
          existingPartners={partners}
          activeTab={activeTab}
          onSave={handleCreateProject}
          onClose={() => setShowNewProject(false)}
        />
      )}

      {/* PARTNER UPDATE MODAL */}
      {editingUpdate && (
        <PartnerUpdateModal
          update={editingUpdate}
          isNew={isNewUpdate}
          dark={dark}
          projectOptions={[...new Set(allData.map(d => `${d.partner} — ${d.product} — ${d.country}`))]}
          onSave={handleSaveUpdate}
          onDelete={handleDeleteUpdate}
          onClose={() => setEditingUpdate(null)}
        />
      )}
    </div>
  );
}